import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

export class ResPDFGenerator {
  static generateInvoice(invoice) {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.setTextColor(245, 158, 11); // Amber
    doc.text('INVOICE', 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Invoice Number: ${invoice.invoiceNumber}`, 20, 35);
    doc.text(`Issue Date: ${invoice.issueDate}`, 20, 40);
    doc.text(`Due Date: ${invoice.dueDate}`, 20, 45);
    
    // Property and Tenant Info
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Property:', 20, 60);
    doc.setFont('helvetica', 'normal');
    doc.text(invoice.propertyName, 20, 67);
    doc.text(invoice.propertyInternalId || '', 20, 72);
    
    doc.setFont('helvetica', 'bold');
    doc.text('Tenant:', 20, 82);
    doc.setFont('helvetica', 'normal');
    doc.text(invoice.tenantName, 20, 89);
    
    // Amount Details
    let yPos = 110;
    doc.autoTable({
      startY: yPos,
      head: [['Description', 'Amount (SAR)']],
      body: [
        ['Monthly Rent', invoice.amount.toLocaleString()],
        invoice.lateFee > 0 ? ['Late Fee', invoice.lateFee.toLocaleString()] : null,
        ['Total Amount', invoice.totalAmount.toLocaleString()],
      ].filter(Boolean),
      theme: 'striped',
      headStyles: { fillColor: [245, 158, 11] },
    });
    
    // Status
    yPos = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`Status: ${invoice.status.toUpperCase()}`, 20, yPos);
    
    if (invoice.paymentDate) {
      doc.text(`Payment Date: ${invoice.paymentDate}`, 20, yPos + 7);
    }
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('Generated by Real Estate Management System', 105, 280, { align: 'center' });
    
    return doc;
  }

  static generateContract(contract, type = 'renting-out') {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.setTextColor(245, 158, 11);
    doc.text('RENTAL CONTRACT', 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Contract Number: ${contract.contractNumber}`, 20, 35);
    doc.text(`Start Date: ${contract.startDate}`, 20, 40);
    doc.text(`End Date: ${contract.endDate}`, 20, 45);
    doc.text(`Duration: ${contract.duration}`, 20, 50);
    
    // Property Info
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Property Information:', 20, 65);
    doc.setFont('helvetica', 'normal');
    doc.text(`Name: ${contract.propertyName}`, 25, 72);
    doc.text(`ID: ${contract.propertyInternalId}`, 25, 77);
    
    // Tenant/Landlord Info
    doc.setFont('helvetica', 'bold');
    doc.text(`${type === 'renting-out' ? 'Tenant' : 'Landlord'} Information:`, 20, 87);
    doc.setFont('helvetica', 'normal');
    doc.text(`Name: ${type === 'renting-out' ? contract.tenantName : contract.landlordName}`, 25, 94);
    doc.text(`Contact: ${type === 'renting-out' ? contract.tenantContact : contract.landlordContact}`, 25, 99);
    doc.text(`Email: ${type === 'renting-out' ? contract.tenantEmail : contract.landlordEmail}`, 25, 104);
    
    // Financial Terms
    let yPos = 115;
    doc.setFont('helvetica', 'bold');
    doc.text('Financial Terms:', 20, yPos);
    yPos += 10;
    
    doc.autoTable({
      startY: yPos,
      head: [['Term', 'Amount (SAR)']],
      body: [
        ['Monthly Rent', contract.monthlyRent.toLocaleString()],
        ['Security Deposit', contract.securityDeposit.toLocaleString()],
        ['Total Contract Value', contract.totalAmount.toLocaleString()],
      ],
      theme: 'striped',
      headStyles: { fillColor: [245, 158, 11] },
    });
    
    // Payment Terms
    yPos = doc.lastAutoTable.finalY + 10;
    doc.setFont('helvetica', 'bold');
    doc.text('Payment Terms:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(contract.paymentTerms, 25, yPos + 7);
    doc.text(`Payment Method: ${contract.paymentMethod}`, 25, yPos + 14);
    
    // Special Terms
    if (contract.specialTerms) {
      yPos += 25;
      doc.setFont('helvetica', 'bold');
      doc.text('Special Terms:', 20, yPos);
      doc.setFont('helvetica', 'normal');
      const specialTermsLines = doc.splitTextToSize(contract.specialTerms, 170);
      doc.text(specialTermsLines, 25, yPos + 7);
    }
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('Generated by Real Estate Management System', 105, 280, { align: 'center' });
    
    return doc;
  }

  static generatePropertyReport(properties) {
    const doc = new jsPDF('landscape');
    
    // Header
    doc.setFontSize(20);
    doc.setTextColor(245, 158, 11);
    doc.text('Properties Report', 148, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 30);
    doc.text(`Total Properties: ${properties.length}`, 20, 35);
    
    // Properties Table
    doc.autoTable({
      startY: 45,
      head: [['ID', 'Name', 'Type', 'City', 'Status', 'Area (sqm)', 'Monthly Rent (SAR)']],
      body: properties.map(p => [
        p.internalId,
        p.name,
        p.type,
        p.city,
        p.status,
        p.area,
        p.monthlyRent.toLocaleString()
      ]),
      theme: 'striped',
      headStyles: { fillColor: [245, 158, 11] },
    });
    
    return doc;
  }
}
